import React, { useState, useEffect } from 'react';
import { Employee, TestAssignment } from '../types/test';
import './AssignTestModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface AssignTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  testId: string;
  testTitle: string;
  onAssigned: () => void;
}

interface AssignmentResult {
  assignmentId: string;
  employeeEmail: string;
  status: string;
  notificationSent: boolean;
  notificationError?: string;
}

const AssignTestModal: React.FC<AssignTestModalProps> = ({
  isOpen,
  onClose,
  testId,
  testTitle,
  onAssigned,
}) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [existingAssignments, setExistingAssignments] = useState<TestAssignment[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [results, setResults] = useState<AssignmentResult[] | null>(null);
  const [errors, setErrors] = useState<{ employeeEmail: string; error: string }[]>([]);

  useEffect(() => {
    if (isOpen) {
      fetchEmployees();
      fetchExistingAssignments();
      // Reset state
      setSelectedEmails([]);
      setNewEmail('');
      setResults(null);
      setErrors([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, testId]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/employees`);
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees || []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingAssignments = async () => {
    try {
      const response = await fetch(`${API_URL}/tests/${testId}/assignments`);
      if (response.ok) {
        const data = await response.json();
        setExistingAssignments(data.assignments || []);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const handleToggleEmployee = (email: string) => {
    setSelectedEmails(prev => 
      prev.includes(email) 
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const handleAddNewEmail = () => {
    if (newEmail && !selectedEmails.includes(newEmail) && newEmail.includes('@')) {
      setSelectedEmails(prev => [...prev, newEmail]);
      setNewEmail('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddNewEmail();
    }
  };

  const handleAssign = async () => {
    if (selectedEmails.length === 0) return;

    setAssigning(true);
    setResults(null);
    setErrors([]);

    try {
      const response = await fetch(`${API_URL}/tests/${testId}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          employeeEmails: selectedEmails,
          dueDate: dueDate || undefined,
          sendNotification,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setResults(data.assignments || []);
        setErrors(data.errors || []);
        
        // Refresh assignments list
        fetchExistingAssignments();
        
        // Clear selection
        setSelectedEmails([]);
        
        // Notify parent
        onAssigned();
      } else {
        setErrors([{ employeeEmail: 'all', error: data.detail || 'Failed to assign test' }]);
      }
    } catch (error) {
      console.error('Error assigning test:', error);
      setErrors([{ employeeEmail: 'all', error: 'Network error. Please try again.' }]);
    } finally {
      setAssigning(false);
    }
  };

  const handleResendNotification = async (assignmentId: string) => {
    try {
      const response = await fetch(`${API_URL}/assignments/${assignmentId}/resend-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason: 'Manual resend from admin UI' }),
      });

      if (response.ok) {
        fetchExistingAssignments();
      } else {
        const data = await response.json();
        alert(`Failed to resend: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error resending notification:', error);
      alert('Failed to resend notification');
    }
  };

  const isAlreadyAssigned = (email: string) => {
    return existingAssignments.some(a => a.employeeEmail === email);
  };

  if (!isOpen) return null;

  return (
    <div className="assign-modal-overlay" onClick={onClose}>
      <div className="assign-modal" onClick={e => e.stopPropagation()}>
        <div className="assign-modal-header">
          <h2>Assign Test</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="assign-modal-content">
          <div className="test-info">
            <span className="label">Test:</span>
            <span className="value">{testTitle}</span>
          </div>

          {/* Due Date */}
          <div className="form-group">
            <label>Due Date (Optional)</label>
            <input
              type="datetime-local"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
            />
          </div>

          {/* Email Notification Toggle */}
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={sendNotification}
                onChange={e => setSendNotification(e.target.checked)}
              />
              Send email notification to assigned employees
            </label>
          </div>

          {/* Employee Selection */}
          <div className="employee-selection">
            <h3>Select Employees</h3>
            
            {/* Add new email */}
            <div className="add-email-row">
              <input
                type="email"
                placeholder="Enter email address..."
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <button 
                type="button" 
                onClick={handleAddNewEmail}
                disabled={!newEmail || !newEmail.includes('@')}
              >
                Add
              </button>
            </div>

            {/* Selected emails */}
            {selectedEmails.length > 0 && (
              <div className="selected-emails">
                <h4>Selected ({selectedEmails.length})</h4>
                <div className="email-chips">
                  {selectedEmails.map(email => (
                    <span key={email} className="email-chip">
                      {email}
                      <button onClick={() => handleToggleEmployee(email)}>×</button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Existing employees */}
            {loading ? (
              <div className="loading">Loading employees...</div>
            ) : employees.length > 0 ? (
              <div className="employee-list">
                <h4>Registered Employees</h4>
                {employees.map(emp => {
                  const assigned = isAlreadyAssigned(emp.email);
                  return (
                    <label 
                      key={emp.email} 
                      className={`employee-item ${assigned ? 'already-assigned' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedEmails.includes(emp.email)}
                        onChange={() => handleToggleEmployee(emp.email)}
                        disabled={assigned}
                      />
                      <span className="employee-info">
                        <span className="name">{emp.name || emp.email}</span>
                        {emp.name && <span className="email">{emp.email}</span>}
                      </span>
                      {assigned && <span className="assigned-badge">Already Assigned</span>}
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="no-employees">No registered employees yet. Add email addresses above.</p>
            )}
          </div>

          {/* Results */}
          {results && results.length > 0 && (
            <div className="assignment-results success">
              <h4>✓ Successfully Assigned</h4>
              {results.map(r => (
                <div key={r.assignmentId} className="result-item">
                  <span>{r.employeeEmail}</span>
                  {r.notificationSent ? (
                    <span className="notification-status sent">Email sent</span>
                  ) : r.notificationError ? (
                    <span className="notification-status error" title={r.notificationError}>
                      Email failed
                    </span>
                  ) : (
                    <span className="notification-status skipped">No email</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {errors.length > 0 && (
            <div className="assignment-results error">
              <h4>⚠ Errors</h4>
              {errors.map((e, i) => (
                <div key={i} className="result-item error">
                  <span>{e.employeeEmail}</span>
                  <span className="error-message">{e.error}</span>
                </div>
              ))}
            </div>
          )}

          {/* Existing Assignments */}
          {existingAssignments.length > 0 && (
            <div className="existing-assignments">
              <h3>Current Assignments ({existingAssignments.length})</h3>
              <div className="assignments-list">
                {existingAssignments.map(a => (
                  <div key={a.id} className="assignment-row">
                    <div className="assignment-info">
                      <span className="employee">{a.employeeName || a.employeeEmail}</span>
                      <span className={`status status-${a.status}`}>{a.status}</span>
                    </div>
                    <div className="assignment-actions">
                      {a.lastNotificationSent ? (
                        <span className="last-sent" title={`Last sent: ${new Date(a.lastNotificationSent).toLocaleString()}`}>
                          📧 Sent
                        </span>
                      ) : null}
                      <button 
                        className="resend-btn"
                        onClick={() => handleResendNotification(a.id)}
                        title="Resend notification email"
                      >
                        ↻ Resend
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="assign-modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
          <button 
            className="btn-primary"
            onClick={handleAssign}
            disabled={selectedEmails.length === 0 || assigning}
          >
            {assigning ? 'Assigning...' : `Assign to ${selectedEmails.length} Employee${selectedEmails.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignTestModal;
